import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

interface ChatMessage {
  id: string;
  message: string;
  created_at: string;
  is_from_client: boolean;
  client_id: number;
  user_id: string;
}

export const ClientChatList = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const { user } = useAuth();
  const { toast } = useToast();
  const [clientId, setClientId] = useState<number | null>(null);

  // First, get or create the client record
  useEffect(() => {
    if (!user?.email) return;

    const setupClient = async () => {
      console.log('Setting up client for email:', user.email);
      
      // First try to find existing client by email
      const { data: existingClient, error: findError } = await supabase
        .from('clients')
        .select('id')
        .eq('email', user.email)
        .single();

      if (findError && findError.code !== 'PGRST116') {
        console.error('Error finding client:', findError);
        return;
      }

      // If client exists, use their ID
      if (existingClient) {
        console.log('Found existing client:', existingClient);
        setClientId(existingClient.id);
        return;
      }

      // If no client exists, create a new one
      const { data: newClient, error: createError } = await supabase
        .from('clients')
        .insert([
          {
            name: user.email?.split('@')[0] || 'New Client',
            email: user.email,
            status: 'Pending',
            source: 'direct'
          }
        ])
        .select('id')
        .single();

      if (createError) {
        console.error('Error creating client:', createError);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to setup chat. Please try again later."
        });
        return;
      }

      console.log('Created new client:', newClient);
      setClientId(newClient.id);
    };

    setupClient();
  }, [user?.email, toast]);

  // Then, fetch and subscribe to messages
  useEffect(() => {
    if (!clientId) return;

    console.log('Fetching messages for client:', clientId);

    const fetchMessages = async () => {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching messages:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load chat messages"
        });
        return;
      }

      console.log('Fetched messages:', data);
      setMessages(data || []);
    };

    // Subscribe to new messages
    const channel = supabase
      .channel(`chat_messages_${clientId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_messages',
          filter: `client_id=eq.${clientId}`
        },
        (payload) => {
          console.log('Real-time update received:', payload);
          if (payload.eventType === 'INSERT') {
            setMessages(current => [...current, payload.new as ChatMessage]);
          }
        }
      )
      .subscribe();

    fetchMessages();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [clientId, toast]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !user || !clientId) return;

    const messageData = {
      message: newMessage.trim(),
      client_id: clientId,
      is_from_client: true,
      user_id: user.id
    };

    console.log('Sending message:', messageData);

    const { error } = await supabase
      .from('chat_messages')
      .insert([messageData]);

    if (error) {
      console.error('Error sending message:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to send message"
      });
      return;
    }

    setNewMessage("");
  };

  if (!clientId) {
    return (
      <div className="flex flex-col h-[600px] bg-[#1A1F2C] rounded-lg border border-gray-700">
        <div className="p-4 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">Setting up chat...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[600px] bg-[#1A1F2C] rounded-lg border border-gray-700">
      <div className="p-4 border-b border-gray-700">
        <h2 className="text-xl font-semibold text-white">Chat with Support</h2>
      </div>

      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${
                message.is_from_client ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`max-w-[80%] p-3 rounded-lg ${
                  message.is_from_client
                    ? "bg-primary text-primary-foreground"
                    : "bg-gray-700 text-white"
                }`}
              >
                <p className="text-sm">{message.message}</p>
                <span className="text-xs opacity-70">
                  {new Date(message.created_at).toLocaleTimeString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-700">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 bg-gray-800 border-gray-600 text-white"
          />
          <Button type="submit" size="icon">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
};